import React, { useEffect, useState } from "react";
import { Card, Table, Badge, Col, Button } from "react-bootstrap";
import PageTitle from "../layouts/PageTitle";
import Spinner from "../common/Spinner";
import Pagination from "../common/Pagination";
import {
  getErrorGroups,
  getErrorGroupDetail,
} from "../../services/ErrorTelemetry/ErrorTelemetryService";

export default function ErrorTelemetry() {
  const [loader, setLoader] = useState(false);
  const [view, setView] = useState("list");

  // List view state
  const [groups, setGroups] = useState([]);
  const [groupPageCount, setGroupPageCount] = useState(0);
  const [groupCurrentPage, setGroupCurrentPage] = useState(0);
  const [groupTotal, setGroupTotal] = useState(0);

  // Detail view state
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventPageCount, setEventPageCount] = useState(0);
  const [eventCurrentPage, setEventCurrentPage] = useState(0);
  const [eventTotal, setEventTotal] = useState(0);
  const [expandedRow, setExpandedRow] = useState(null);

  const limit = 10;

  const fetchGroups = async () => {
    setLoader(true);
    try {
      const response = await getErrorGroups(groupCurrentPage, limit);
      setGroups(response.data.data);
      const total = response.data.totalRecords;
      setGroupTotal(total);
      setGroupPageCount(Math.ceil(total / limit));
    } catch (error) {
      console.log(error, "error");
    } finally {
      setLoader(false);
    }
  };

  const fetchGroupDetail = async (groupId, page = 0) => {
    setLoader(true);
    try {
      const response = await getErrorGroupDetail(groupId, page, limit);
      const data = response.data.data;
      setSelectedGroup(data.group);
      setEvents(data.events);
      const total = data.totalRecords;
      setEventTotal(total);
      setEventPageCount(Math.ceil(total / limit));
    } catch (error) {
      console.log(error, "error");
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    if (view === "list") {
      fetchGroups();
    }
  }, [groupCurrentPage]);

  useEffect(() => {
    if (view === "detail" && selectedGroup) {
      fetchGroupDetail(selectedGroup._id, eventCurrentPage);
    }
  }, [eventCurrentPage]);

  const handleGroupClick = (group) => {
    setSelectedGroup(group);
    setEventCurrentPage(0);
    setExpandedRow(null);
    setView("detail");
    fetchGroupDetail(group._id, 0);
  };

  const handleBackToList = () => {
    setView("list");
    setSelectedGroup(null);
    setEvents([]);
    setEventCurrentPage(0);
    setExpandedRow(null);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleString();
  };

  const getCountBadge = (count) => {
    if (count >= 50) return <Badge variant="danger light">{count}</Badge>;
    if (count >= 10) return <Badge variant="warning light">{count}</Badge>;
    return <Badge variant="info light">{count}</Badge>;
  };

  if (view === "detail" && selectedGroup) {
    return (
      <div>
        <PageTitle activeMenu="Error Group Detail" motherMenu="Telemetry" />
        <Col>
          <Card>
            <Card.Header className="d-block">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleBackToList}
                  >
                    &larr; Back to Groups
                  </Button>
                </div>
              </div>
              <div className="mt-3">
                <h5 className="mb-1">{selectedGroup.screen}</h5>
                <p className="mb-1 text-muted">
                  <strong>Action:</strong> {selectedGroup.action}
                </p>
                <p className="mb-1 text-muted">
                  <strong>Error Code:</strong> {selectedGroup.errorCode}
                </p>
                <p className="mb-0 text-muted">
                  <strong>Total Count:</strong> {selectedGroup.totalCount} &nbsp;|&nbsp;
                  <strong>Companies Affected:</strong> {selectedGroup.affectedCompaniesCount}
                </p>
              </div>
            </Card.Header>
            <Card.Body>
              {loader ? (
                <Spinner />
              ) : (
                <Table responsive>
                  <thead className="text-black">
                    <tr>
                      <th><strong>TIMESTAMP</strong></th>
                      <th><strong>COMPANY</strong></th>
                      <th><strong>PLATFORM</strong></th>
                      <th><strong>APP VERSION</strong></th>
                      <th><strong>ERROR MESSAGE</strong></th>
                      <th><strong>API ENDPOINT</strong></th>
                      <th><strong>RESPONSE</strong></th>
                    </tr>
                  </thead>
                  <tbody>
                    {events?.map((event, i) => (
                      <React.Fragment key={i}>
                        <tr>
                          <td style={{ whiteSpace: "nowrap" }}>{formatDate(event.createdAt)}</td>
                          <td>{event.companyId?.name || "—"}</td>
                          <td>{event.platform}</td>
                          <td>{event.appVersion}</td>
                          <td style={{ maxWidth: "250px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {event.errorMessage || "—"}
                          </td>
                          <td>{event.apiEndpoint || "—"}</td>
                          <td>
                            {event.apiResponse?.body ? (
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => setExpandedRow(expandedRow === i ? null : i)}
                              >
                                {expandedRow === i ? "Hide" : "View"}
                              </Button>
                            ) : (
                              "—"
                            )}
                          </td>
                        </tr>
                        {expandedRow === i && event.apiResponse?.body && (
                          <tr>
                            <td colSpan="7">
                              <pre style={{
                                background: "#f8f9fa",
                                padding: "10px",
                                borderRadius: "4px",
                                maxHeight: "200px",
                                overflow: "auto",
                                fontSize: "12px",
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-all",
                              }}>
                                {event.apiResponse.body}
                              </pre>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </Table>
              )}
              {events?.length === 0 && !loader && (
                <div className="justify-content-center d-flex my-5">
                  No occurrences found.
                </div>
              )}
              {events?.length !== 0 && (
                <div className="card-footer clearfix">
                  <div className="d-block d-sm-flex justify-content-between align-items-center">
                    <div className="dataTables_info">
                      Showing {eventCurrentPage * limit + 1} to{" "}
                      {eventTotal > (eventCurrentPage + 1) * limit
                        ? (eventCurrentPage + 1) * limit
                        : eventTotal}{" "}
                      of {eventTotal} entries
                    </div>
                    <Pagination
                      pageCount={eventPageCount}
                      pageValue={eventCurrentPage}
                      setPage={setEventCurrentPage}
                    />
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </div>
    );
  }

  return (
    <div>
      <PageTitle activeMenu="Error Groups" motherMenu="Telemetry" />
      <Col>
        <Card>
          <Card.Body>
            {loader ? (
              <Spinner />
            ) : (
              <Table responsive>
                <thead className="text-black">
                  <tr>
                    <th><strong>SCREEN</strong></th>
                    <th><strong>ACTION</strong></th>
                    <th><strong>ERROR CODE</strong></th>
                    <th><strong>COUNT</strong></th>
                    <th><strong>COMPANIES AFFECTED</strong></th>
                    <th><strong>LAST OCCURRED</strong></th>
                  </tr>
                </thead>
                <tbody>
                  {groups?.map((group, i) => (
                    <tr
                      key={i}
                      style={{ cursor: "pointer" }}
                      onClick={() => handleGroupClick(group)}
                    >
                      <td>{group.screen}</td>
                      <td>{group.action}</td>
                      <td>{group.errorCode}</td>
                      <td>{getCountBadge(group.totalCount)}</td>
                      <td>{group.affectedCompaniesCount}</td>
                      <td style={{ whiteSpace: "nowrap" }}>{formatDate(group.lastOccurredAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
            {groups?.length === 0 && !loader && (
              <div className="justify-content-center d-flex my-5">
                No error groups found.
              </div>
            )}
            {groups?.length !== 0 && (
              <div className="card-footer clearfix">
                <div className="d-block d-sm-flex justify-content-between align-items-center">
                  <div className="dataTables_info">
                    Showing {groupCurrentPage * limit + 1} to{" "}
                    {groupTotal > (groupCurrentPage + 1) * limit
                      ? (groupCurrentPage + 1) * limit
                      : groupTotal}{" "}
                    of {groupTotal} entries
                  </div>
                  <Pagination
                    pageCount={groupPageCount}
                    pageValue={groupCurrentPage}
                    setPage={setGroupCurrentPage}
                  />
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
      </Col>
    </div>
  );
}
